import { useContext, useEffect, useRef } from 'react';
import { UNSAFE_DataRouterContext, useBlocker } from 'react-router-dom';

import { useCmsInteractions } from './use-cms-interactions';

type Props = { dirty: boolean; pendingForms: boolean; busy: boolean; save: () => Promise<boolean> };
export default function SiteCmsNavigationGuard(props: Props) {
  const dataRouter = useContext(UNSAFE_DataRouterContext);
  useEffect(() => {
    if (!props.dirty && !props.pendingForms && !props.busy) return;
    const unload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', unload);
    return () => window.removeEventListener('beforeunload', unload);
  }, [props.dirty, props.pendingForms, props.busy]);
  return dataRouter ? <RouterGuard {...props} /> : null;
}
function RouterGuard(props: Props) {
  const { ask } = useCmsInteractions();
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      (props.dirty || props.pendingForms || props.busy) &&
      (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search),
  );
  const latest = useRef({ ...props, blocker });
  const attempt = useRef<{ key: string } | null>(null);
  useEffect(() => {
    latest.current = { ...props, blocker };
  }, [props, blocker]);
  useEffect(
    () => () => {
      attempt.current = null;
    },
    [],
  );
  useEffect(() => {
    if (blocker.state !== 'blocked') {
      attempt.current = null;
      return;
    }
    const key = blocker.location.key;
    if (attempt.current?.key === key) return;
    const token = { key };
    attempt.current = token;
    const active = () =>
      attempt.current === token &&
      latest.current.blocker.state === 'blocked' &&
      latest.current.blocker.location.key === key;
    const reset = () => {
      if (active()) latest.current.blocker.reset?.();
    };
    const proceed = () => {
      if (active()) latest.current.blocker.proceed?.();
    };
    void (async () => {
      const state = latest.current;
      if (state.busy) {
        await ask({
          title: 'Une opération est en cours',
          message:
            'Attendez son résultat avant de quitter le CMS. Aucun enregistrement ni import ne sera annulé silencieusement.',
          choices: [],
        });
        reset();
        return;
      }
      const result = await ask({
        title: 'Quitter la gestion du site ?',
        message: state.pendingForms
          ? 'Un formulaire contient une saisie non validée. Terminez-le ou abandonnez-le explicitement ; enregistrer le brouillon ne sauvegarde pas ces champs.'
          : 'Votre travail n’est pas encore enregistré. La version publique restera inchangée.',
        choices: [
          ...(!state.pendingForms && state.dirty ? [{ value: 'save', label: 'Enregistrer et quitter' }] : []),
          { value: 'discard', label: 'Abandonner et quitter', danger: true },
        ],
      });
      if (!active()) return;
      if (result === 'discard' && !latest.current.busy) {
        window.dispatchEvent(new Event('cms:discard-local-copy'));
        proceed();
        return;
      }
      if (result === 'save') {
        const saved = await latest.current.save();
        // Wait for the reducer and activity registrations to commit before leaving.
        await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
        if (saved && !latest.current.dirty && !latest.current.pendingForms && !latest.current.busy) {
          proceed();
          return;
        }
      }
      reset();
    })().catch(reset);
    // Deliberately no dependency-cleanup cancellation: a save/typing rerender
    // must not invalidate the already open decision. Only unmount/route changes do.
  }, [blocker, ask]);
  return null;
}
